import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { inject, observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import i18n from "i18next";
import { Trans } from "react-i18next";
import type { IHeartAppsFlyerSDK } from "@iheartradio/web.appsflyer-sdk";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Modal } from "../ui/Modal.component";
import { Button, ButtonKind } from "../ui/Button.component";
import { AppStoreBadge } from "../components/icons/AppStoreBadge.component";
import { GooglePlayBadge } from "../components/icons/GooglePlayBadge.component";
import { TalkbackMobileBanner } from "../components/icons/TalkbackMobileBanner.component";
import styles from "./HeaderTalkbackModal.module.scss";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("HeaderTalkbackModal");

const renderMobileContent = (stationName: string, onDownload: () => void, onClose: () => void): React.ReactNode => (
  <>
    <div className={styles.banner}>
      <TalkbackMobileBanner />
    </div>

    <div className={styles.container}>
      <span className={styles.title}>{i18n.t("talkback_modal_title")}</span>

      <div className={styles.mobileBody}>
        <span className={styles.description}>
          <Trans
            i18nKey="talkback_modal_mobile_description"
            values={{ stationName }}
            components={{ bold: <strong /> }}
          />
        </span>

        <div className={styles.buttonContainer}>
          <Button className={styles.appButton} click={onDownload} kind={ButtonKind.PRIMARY}>
            {i18n.t("talkback_download_free")}
          </Button>

          <Button className={styles.linkButton} click={onClose} kind={ButtonKind.CUSTOMIZED}>
            {i18n.t("talkback_no_thanks")}
          </Button>
        </div>
      </div>
    </div>
  </>
);

const renderDesktopContent = (
  stationName: string,
  qrContainerRef: React.RefObject<HTMLDivElement>,
  isLoading: boolean,
  onAppStoreClick: () => void,
): React.ReactNode => (
  <>
    <span className={styles.title}>{i18n.t("talkback_modal_title")}</span>

    <div className={styles.desktopBody}>
      <p className={styles.description}>
        <Trans
          i18nKey="talkback_modal_desktop_description"
          values={{ stationName }}
          components={{ bold: <strong /> }}
        />
      </p>

      <div className={styles.qrContainer} ref={qrContainerRef}>
        {isLoading && (
          <div className={styles.loading}>
            <div className={styles.spinner} />
          </div>
        )}
      </div>

      <div className={styles.appBadges}>
        <button onClick={onAppStoreClick} aria-label="Download on the App Store" className={styles.appBadgeButton}>
          <AppStoreBadge />
        </button>
        <button onClick={onAppStoreClick} aria-label="Get it on Google Play" className={styles.appBadgeButton}>
          <GooglePlayBadge />
        </button>
      </div>
    </div>
  </>
);

interface HeaderTalkbackModalProps {
  open: boolean;
  onClose: () => void;
  store?: Store;
}

export const HeaderTalkbackModal = inject("store")(
  observer(({ open, onClose, store }: HeaderTalkbackModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [qrCodeGenerated, setQrCodeGenerated] = useState(false);
    const qrContainerRef = useRef<HTMLDivElement>(null);

    if (!store) {
      return null;
    }

    const { site, device } = store;
    const isMobile = device.deviceType === "mobile";
    const { search } = useLocation();

    // const stationId = site.index.slug;
    const streamId = site.index.stream?.id;
    const stationName = site.sections?.general?.name || site.index.summary?.name || "";

    const sdk = (window as any).iHeartAppsFlyer as IHeartAppsFlyerSDK | undefined;

    useEffect(() => {
      if (!sdk) {
        log.warn("AppsFlyer SDK not available");
      }
    }, [sdk]);

    const handleClose = useCallback(() => {
      store.onAnalyticsAction.dispatch({
        sectionName: "header_talkback_modal",
        pageName: store.page.currentPage?.name || "",
        context: isMobile ? "mobile_modal_close" : "desktop_modal_close",
        action: "click",
      });
      onClose();
    }, [store, onClose, isMobile]);

    const generateQRCode = useCallback(() => {
      if (!qrContainerRef.current || !sdk) {
        log.warn("Cannot generate QR code - SDK missing");
        return;
      }

      setIsLoading(true);
      try {
        const qrElementId = qrContainerRef.current.id || `talkback-qr-${Date.now()}`;
        if (!qrContainerRef.current.id) {
          qrContainerRef.current.id = qrElementId;
        }

        const deepLinkUrl = sdk.buildTalkbackDeepLinkUrl("live", streamId, search);

        sdk.generateQRCode(
          qrElementId,
          {
            campaign: "Talkback",
            afParameters: {
              channel: "qr",
              ad: "header_button",
              afSub1: store.page.currentPage?.name || "unknown",
              afSub3: "talkback_modal_qr",
              // Use Universal Links instead of custom scheme for better iOS compatibility
              afDp: deepLinkUrl,
              deepLinkValue: deepLinkUrl,
            },
          },
          {
            size: 172,
            codeColor: "#000",
            // codeColor: site.sections?.design?.accent_color || "#000",
            backgroundColor: "#fff",
          },
        );
        setQrCodeGenerated(true);
      } catch (error) {
        log.error("Failed to generate QR code", error);
      } finally {
        setIsLoading(false);
      }
    }, [sdk, store, streamId, search]);

    const handleOneLinkUrl = useCallback(() => {
      if (!sdk) {
        log.warn("SDK not available");
        return;
      }

      try {
        const deepLinkUrl = sdk.buildTalkbackDeepLinkUrl("live", streamId, search);

        const url = sdk.generateClickURL({
          campaign: "Talkback",
          afParameters: {
            channel: isMobile ? "direct" : "qr",
            ad: isMobile ? "mobile_download_button" : "app_store_badge",
            afSub1: store.page.currentPage?.name || "unknown",
            afSub3: isMobile ? "talkback_modal_mobile" : "talkback_modal_desktop",
            // Use Universal Links instead of custom scheme for better iOS compatibility
            afDp: deepLinkUrl,
            deepLinkValue: deepLinkUrl,
          },
        });

        if (url) {
          store.onAnalyticsAction.dispatch({
            sectionName: "header_talkback_modal",
            pageName: store.page.currentPage?.name || "",
            context: isMobile ? "mobile_download_click" : "desktop_badge_click",
            action: "click",
          });

          window.open(url, "_blank");

          if (isMobile) {
            setTimeout(() => {
              onClose();
            }, 1000);
          }
        }
      } catch (error) {
        log.error("Failed to generate app store URL", error);
      }
    }, [sdk, store.onAnalyticsAction, store.page.currentPage, isMobile, onClose, streamId, search]);

    useEffect(() => {
      if (open) {
        if (!isMobile && qrContainerRef.current && !qrCodeGenerated) {
          generateQRCode();
        }

        store.onAnalyticsAction.dispatch({
          sectionName: "header_talkback_modal",
          pageName: store.page.currentPage?.name || "",
          context: isMobile ? "mobile_modal_open" : "desktop_modal_open",
          action: "click",
        });
      }

      const qrElementId = qrContainerRef.current?.id;

      return () => {
        if (!open) {
          if (qrCodeGenerated && qrElementId && window.iHeartAppsFlyer) {
            window.iHeartAppsFlyer.clearQRCode(qrElementId);
            setQrCodeGenerated(false);
          }
        }
      };
    }, [open, isMobile, qrCodeGenerated, generateQRCode, store]);

    const modalContent = useMemo(
      () =>
        isMobile
          ? renderMobileContent(stationName, handleOneLinkUrl, handleClose)
          : renderDesktopContent(stationName, qrContainerRef, isLoading, handleOneLinkUrl),
      [isMobile, stationName, handleOneLinkUrl, handleClose, isLoading],
    );

    return (
      <Modal closeButtonSize={32} open={open} callback={handleClose} addClass={styles.talkbackModal}>
        <div className={styles.modalContent}>{modalContent}</div>
      </Modal>
    );
  }),
);
